import {externalApiURL} from '@yandex-delivery/client-core/api/corp'
import {fetch, FetchError, ServerError} from '@logistics-frontend/core/utils/network'
import type {LaunchResponse} from '@logistics-frontend/types/schemas/services/cargo-corp/docs/yaml/definitions/launch'
import {addQuery, constant, firstOf, flow, get, ifThen, isEmpty} from '@yandex-delivery/utils'
import type {PaymentCard} from '@yandex-delivery/api/model/payment'
import {createCargoCorpRepository} from '@yandex-delivery/api/repo/cargo-corp/external'
import {CargoCrmRepository} from '@yandex-delivery/api/repo/cargo-crm'
import {createDCAARepository} from '@yandex-delivery/api/repo/dcaa'
import {createPassportRepository} from '@yandex-delivery/api/repo/passport'
import {createPaymentCardService} from '@yandex-delivery/api/service/payment'
import {createRegistrationService} from '@yandex-delivery/api/service/registration'
import type {
  PaymentMethodsListRequest,
  PaymentMethodsListResponse,
} from '@yandex-delivery/types/schemas/services/delivery-corp-account-api/definitions/payment_methods'
import {useCallback} from 'react'

import {useConfigContext} from '../../utils/contexts/ConfigContext'
import {useFetch} from './useFetch'

const cargoCorpRepository = createCargoCorpRepository({
  fetch,
})
const dcaaRepository = createDCAARepository({fetch})

function fetchPaymentMethods(body: PaymentMethodsListRequest, country?: string): Promise<PaymentMethodsListResponse> {
  return fetch<PaymentMethodsListRequest, PaymentMethodsListResponse, PaymentMethodsListResponse>({
    method: 'POST',
    url: addQuery(externalApiURL('cargo/v1/corp/employee/payment-methods/list'), {country}),
    body,
  })
}

export const enum PaymentCardErrorCode {
  UNAUTHORIZED = 'unauthorized',
  EMPTY_LOGIN = 'empty_login',
  EMPTY_PHONE = 'empty_phone',
}

const isEmptyLaunch: (r?: LaunchResponse) => boolean = isEmpty
const isEmptyLogin: (r?: LaunchResponse) => boolean = flow(get('passport_account.yandex_login'), isEmpty)
const isPhoneEmpty: (r?: LaunchResponse) => boolean = flow(get('passport_account.details.phones'), isEmpty)

const getUnauthorizedMessage: (r?: LaunchResponse) => PaymentCardErrorCode | undefined = firstOf(
  ifThen(isEmptyLaunch, constant(PaymentCardErrorCode.UNAUTHORIZED)),
  ifThen(isEmptyLogin, constant(PaymentCardErrorCode.EMPTY_LOGIN)),
  ifThen(isPhoneEmpty, constant(PaymentCardErrorCode.EMPTY_PHONE)),
)

type PaymentCardRequestProps = {
  onSuccess(card: PaymentCard): void | Promise<void>
}

export const usePaymentCardService = () => {
  const {countryCode, launch, trustHost} = useConfigContext()

  const {fetchBindingUrl, fetchBoundedCard, fetchCards} = createPaymentCardService({
    cargoCorpRepository,
    dcaaRepository,
    config: {
      country: countryCode,
      trustDomain: trustHost,
    },
  })

  const fetchPaymentCard = useCallback<(props: PaymentCardRequestProps) => Promise<PaymentCard | undefined>>(
    async ({onSuccess}) => {
      const cardErrorCode = getUnauthorizedMessage(launch)

      // если ланч пустой, значит пользователь не авторизован и смысла в походе в бекенд нет
      if (cardErrorCode) {
        const error = new FetchError(cardErrorCode, {
          text: cardErrorCode,
        })

        // @ts-expect-error Недостаточно данных для конструктора данной ошибки
        throw new ServerError({
          error,
          status: 401,
        })
      }

      const [boundedCard, paymentMethod] = await Promise.all([
        fetchBoundedCard(),
        fetchPaymentMethods({mode: 'employee'}, countryCode),
      ])

      const attachedPaymentMethod = paymentMethod?.methods.find(method => method.is_selected)

      if (boundedCard) {
        const card = Object.assign(boundedCard, {
          method_id: attachedPaymentMethod?.id,
          type: attachedPaymentMethod?.display?.type,
          kind: paymentMethod?.selected_method.payment_method_kind,
          has_disable_reason: attachedPaymentMethod?.details.is_disabled,
        })

        await onSuccess(card)

        return card
      }

      return boundedCard
    },
    [countryCode, fetchBoundedCard, launch],
  )

  return {
    fetchBindingUrl,
    fetchBoundedCard,
    fetchCards,
    fetchPaymentCard,
    fetchPaymentMethods,
  }
}

export const useRegistrationService = () => {
  const {passportFetch} = useFetch()

  const passportRepository = createPassportRepository({
    fetch: passportFetch,
  })

  return createRegistrationService({
    corpRepository: cargoCorpRepository,
    crmRepository: CargoCrmRepository,
    passportRepository,
  })
}
